import {nextTick, Ref, ref} from "vue";
import type {Component} from "vue";
import {BroadcastNotificationProps, DetectionNotificationProps, DetectionPatient} from "@/notification/interfaces";
import notificationService from "@/core/services/notificationService";
import {storageService} from "@/container";
import {UserSettings} from "@/settings/interfaces";
import {useNotificationStore} from "@/stores/notification.store";
import {
    expandedBounds,
    NOTIFICATION_WINDOW_WIDTH,
    resolvePlacement,
} from "@/notification/services/notificationWindowPlacement";
import {supportsWindowPlacement} from "@/core/services/shellCapabilities";

/** Room kept under the ADAC flyout, so its shadow is not the last pixel of the window. */
const FLYOUT_MARGIN = 8;

const useNotification = (notificationRef: Ref<HTMLElement | null>) => {
    const item = ref<DetectionNotificationProps | BroadcastNotificationProps | null>(null);
    const component = ref<Component | null>(null);
    const detecting = ref<DetectionPatient|null>(null);

    /**
     * Measure the natural content height. The detection panel caps itself at
     * max-h-screen (the current window height) and scrolls, so its scrollHeight
     * can never exceed a collapsed window: measure its uncapped inner wrapper
     * when present, and fall back on the root for other components.
     *
     * The handle column is measured too and wins when it is the taller of the
     * two: it hangs its own entries — the Vitale card, ADAC — under the strip,
     * and a short panel would otherwise cut the last one off. So does the ADAC
     * flyout, when the tab is unfolded: it hangs beside the panel, outside the
     * content box, and its bottom is where the window has to reach.
     */
    const contentHeight = (): number | undefined => {
        const panelContent = notificationRef.value
            ?.querySelector<HTMLElement>('[data-notification-content]');
        const strip = notificationRef.value
            ?.querySelector<HTMLElement>('[data-notification-strip]');
        const flyout = notificationRef.value
            ?.querySelector<HTMLElement>('[data-notification-flyout]');

        const panel = panelContent?.offsetHeight || notificationRef.value?.scrollHeight;

        if (panel === undefined) {
            return undefined;
        }

        return Math.max(
            panel,
            strip?.offsetHeight ?? 0,
            flyout ? flyout.offsetTop + flyout.offsetHeight + FLYOUT_MARGIN : 0,
        );
    };

    /**
     * Show the notification window after next tick.
     *
     * The window bounds are computed here, from the user settings placement:
     * the main process only applies them and shows the window. On older apps
     * without the placement primitives, the main process positions the window
     * itself from the height and the position setting.
     *
     * @param height
     */
    const showNotification = async (height?: number) => {
        await nextTick();
        const notificationHeight = height || contentHeight();

        if (!supportsWindowPlacement() || !notificationHeight) {
            notificationService().showNotification(notificationHeight);
            return;
        }

        const store = useNotificationStore();
        const settings = await storageService().read('userSettings') as UserSettings | null;
        const context = await window.ipcRenderer.getScreenContext!();
        const placement = resolvePlacement(settings);
        const bounds = expandedBounds(context.display, placement, {
            width: NOTIFICATION_WINDOW_WIDTH,
            // Cap on the display: taller content scrolls inside the panel.
            height: Math.min(notificationHeight, context.display.bounds.height),
        });

        store.applyPlacementSettings(settings);
        store.expandedBounds = bounds;

        notificationService().showNotification(notificationHeight, bounds);
    };

    /**
     * Hide the notification window after next tick.
     */
    const hideNotification = async () => {
        notificationService().hideNotification();
    };

    /**
     * Resize the notification window after next tick.
     */
    const resizeNotification = async () => {
        await nextTick();
        const height = contentHeight() || 0;
        window.ipcRenderer.resizeNotificationWindow(height);
    };

    return {
        notificationRef,
        item,
        component,
        detecting,
        showNotification,
        hideNotification,
        resizeNotification
    };
};

export default useNotification;
